import React, { useMemo } from 'react';
import ReactFlow, { Background, Controls } from 'reactflow';
import 'reactflow/dist/style.css';
import { Box } from '@chakra-ui/react';

const iconMap = {
  aws: {
    compute: 'https://cdn.worldvectorlogo.com/logos/aws-ec2.svg',
    database: 'https://cdn.worldvectorlogo.com/logos/aws-rds.svg',
    storage: 'https://cdn.worldvectorlogo.com/logos/aws-s3.svg',
    loadbalancer: 'https://cdn.worldvectorlogo.com/logos/aws-elastic-load-balancing.svg',
    vpc: '🔒',
    subnet: '📡',
    gateway: 'https://cdn.worldvectorlogo.com/logos/amazon-api-gateway.svg',
    nat: '🌐',
    routetable: '🗺️',
    securitygroup: '🛡️',
    lambda: 'https://cdn.worldvectorlogo.com/logos/aws-lambda-1.svg',
    eks: 'https://cdn.worldvectorlogo.com/logos/aws-eks.svg',
    internet: '🌍'
  },
  azure: {
    compute: 'https://cdn.worldvectorlogo.com/logos/azure-1.svg',
    database: 'https://cdn.worldvectorlogo.com/logos/azure-sql-database.svg',
    storage: 'https://cdn.worldvectorlogo.com/logos/azure-storage.svg',
    loadbalancer: '⚖️',
    vpc: '🔒',
    subnet: '📡',
    gateway: '🚪',
    nat: '🌐',
    routetable: '🗺️',
    securitygroup: '🛡️',
    aks: 'https://cdn.worldvectorlogo.com/logos/azure-1.svg',
    internet: '🌍'
  },
  gcp: {
    compute: 'https://cdn.worldvectorlogo.com/logos/google-compute-engine.svg',
    database: 'https://cdn.worldvectorlogo.com/logos/google-cloud-1.svg',
    storage: 'https://cdn.worldvectorlogo.com/logos/google-cloud-storage.svg',
    loadbalancer: '⚖️',
    vpc: '🔒',
    subnet: '📡',
    gateway: '🚪',
    nat: '🌐',
    routetable: '🗺️',
    securitygroup: '🛡️',
    gke: 'https://cdn.worldvectorlogo.com/logos/google-cloud-1.svg',
    internet: '🌍'
  },
  oracle: {
    compute: '💻',
    database: '🗄️',
    storage: '📦',
    loadbalancer: '⚖️',
    vpc: '🔒',
    subnet: '📡',
    gateway: '🚪',
    nat: '🌐',
    routetable: '🗺️',
    securitygroup: '🛡️',
    internet: '🌍'
  }
};

const CustomNode = ({ data }) => {
  const icon = data.icon;
  const isEmoji = typeof icon === 'string' && icon.length <= 2;

  return (
    <Box
      bg="white"
      border="3px solid"
      borderColor={data.borderColor || '#3182ce'}
      borderRadius="12px"
      p={3}
      minW="120px"
      textAlign="center"
      boxShadow="lg"
    >
      {isEmoji ? (
        <Box fontSize="36px" mb={2}>{icon}</Box>
      ) : (
        <img src={icon} alt={data.label} style={{ width: '50px', height: '50px', margin: '0 auto 8px' }} onError={(e) => e.target.style.display = 'none'} />
      )}
      <Box fontSize="13px" fontWeight="bold" color="gray.800">{data.label}</Box>
    </Box>
  );
};

const GroupNode = ({ data }) => {
  return (
    <Box
      bg={data.bgColor || 'rgba(66, 153, 225, 0.1)'}
      border="3px dashed"
      borderColor={data.borderColor || '#4299e1'}
      borderRadius="16px"
      p={6}
      minW={data.width || '400px'}
      minH={data.height || '300px'}
    >
      <Box fontSize="16px" fontWeight="bold" color="gray.700" mb={2} bg="white" display="inline-block" px={3} py={1} borderRadius="md">{data.label}</Box>
    </Box>
  );
};

const nodeTypes = {
  custom: CustomNode,
  group: GroupNode
};

export default function CloudArchitectureDiagram({ diagramData, provider }) {
  const icons = iconMap[provider] || iconMap.aws;

  const { nodes, edges } = useMemo(() => {
    if (!diagramData?.nodes) return { nodes: [], edges: [] };

    const groups = diagramData.groups || [];
    const flowNodes = [];
    
    // Add groups with better positioning
    groups.forEach((group) => {
      const isVpc = group.type === 'vpc';
      const isSubnet = group.type === 'subnet';
      const isPublic = group.id === 'subnet-pub';
      
      flowNodes.push({
        id: group.id,
        type: 'group',
        position: { 
          x: isVpc ? 100 : (isPublic ? 150 : 700), 
          y: isVpc ? 150 : 250
        },
        data: { 
          label: group.label,
          width: isVpc ? '1200px' : '450px',
          height: isVpc ? '700px' : '500px',
          bgColor: isVpc ? 'rgba(66, 153, 225, 0.05)' : (isPublic ? 'rgba(72, 187, 120, 0.08)' : 'rgba(237, 137, 54, 0.08)'),
          borderColor: isVpc ? '#4299e1' : (isPublic ? '#48bb78' : '#ed8936')
        },
        style: { zIndex: isVpc ? 0 : 1 }
      });
    });

    // Position nodes with clear spacing
    const nodePositions = {
      internet: { x: 600, y: 50, parent: null },
      igw: { x: 250, y: 180, parent: 'vpc' },
      nat: { x: 200, y: 350, parent: 'subnet-pub' },
      alb: { x: 200, y: 450, parent: 'subnet-pub' },
      compute: { x: 200, y: 550, parent: 'subnet-pub' },
      container: { x: 350, y: 450, parent: 'subnet-pub' },
      database: { x: 200, y: 400, parent: 'subnet-priv' },
      storage: { x: 600, y: 800, parent: null },
      sg: { x: 600, y: 180, parent: 'vpc' },
      eks: { x: 350, y: 450, parent: 'subnet-pub' },
      aks: { x: 350, y: 450, parent: 'subnet-pub' },
      gke: { x: 350, y: 450, parent: 'subnet-pub' }
    };

    diagramData.nodes.forEach((node) => {
      const pos = nodePositions[node.id] || { x: 300, y: 300, parent: node.parent };
      
      flowNodes.push({
        id: node.id,
        type: 'custom',
        position: { x: pos.x, y: pos.y },
        data: { 
          label: node.label,
          icon: icons[node.type] || icons.compute,
          borderColor: node.type === 'internet' ? '#48bb78' : 
                       node.type === 'database' ? '#ed8936' : 
                       node.type === 'storage' ? '#9f7aea' : '#3182ce'
        },
        parentNode: pos.parent || node.parent,
        extent: (pos.parent || node.parent) ? 'parent' : undefined,
        style: { zIndex: 10 }
      });
    });

    const flowEdges = (diagramData.edges || []).map((edge, idx) => ({
      id: `e${idx}`,
      source: edge.from,
      target: edge.to,
      animated: true,
      type: 'smoothstep',
      style: { stroke: '#4299e1', strokeWidth: 2.5 }
    }));

    return { nodes: flowNodes, edges: flowEdges };
  }, [diagramData, icons]);

  return (
    <Box h="100%" w="100%" bg="rgba(0,0,0,0.2)" borderRadius="md">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.5}
        maxZoom={1.5}
      >
        <Background color="#555" gap={20} />
        <Controls />
      </ReactFlow>
    </Box>
  );
}
